import { useEffect, useState } from "react";
import { api } from "../lib/config";
import ProductCard from "./ProductCard";
import { Tabs, TabsList, TabsTrigger } from "./ui/tabs";
import { Skeleton } from "./ui/skeleton";
import { Flame, Droplets, Layers } from "lucide-react";

const FILTERS = [
    { value: "all", label: "All", icon: Layers },
    { value: "flower", label: "Flower", icon: Flame },
    { value: "concentrate", label: "Concentrate", icon: Droplets },
];

export default function ProductGrid() {
    const [products, setProducts] = useState([]);
    const [filter, setFilter] = useState("all");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        const fetchProducts = async () => {
            setLoading(true);
            try {
                const { data } = await api.get("/products");
                if (!cancelled) setProducts(data);
            } catch (e) {
                console.error(e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        fetchProducts();
        return () => {
            cancelled = true;
        };
    }, []);

    const visible =
        filter === "all"
            ? products
            : products.filter((p) => p.category === filter);

    return (
        <section
            data-testid="menu-section"
            id="menu"
            className="relative py-20 sm:py-28 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
        >
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
                <div>
                    <p className="font-mono text-xs uppercase tracking-[0.3em] text-highway-red mb-3">
                        // The Menu — Mile Marker 95
                    </p>
                    <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tighter text-balance">
                        Pick your{" "}
                        <span className="italic font-light">cruise</span>.
                    </h2>
                    <p className="mt-4 text-white/60 max-w-xl">
                        Three tiers of premium THCA flower and two grades of
                        concentrate. Tap a size to see the price. Tap{" "}
                        <span className="text-white font-semibold">Order</span>{" "}
                        to slide into our Telegram.
                    </p>
                </div>

                <Tabs
                    value={filter}
                    onValueChange={setFilter}
                    className="lg:self-end"
                >
                    <TabsList className="bg-card border border-border rounded-full p-1 h-auto">
                        {FILTERS.map((f) => {
                            const Icon = f.icon;
                            return (
                                <TabsTrigger
                                    key={f.value}
                                    value={f.value}
                                    data-testid={`filter-${f.value}`}
                                    className="rounded-full px-4 py-2 data-[state=active]:bg-white data-[state=active]:text-black text-white/70 font-medium text-sm"
                                >
                                    <Icon className="w-3.5 h-3.5 mr-1.5" />
                                    {f.label}
                                </TabsTrigger>
                            );
                        })}
                    </TabsList>
                </Tabs>
            </div>

            {loading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {Array.from({ length: 6 }).map((_, i) => (
                        <Skeleton
                            key={i}
                            className="h-[440px] w-full rounded-2xl bg-card"
                        />
                    ))}
                </div>
            ) : visible.length === 0 ? (
                <div
                    data-testid="empty-products"
                    className="py-20 text-center text-white/50 font-mono uppercase text-sm tracking-widest"
                >
                    No products in this lane yet.
                </div>
            ) : (
                <div
                    data-testid="products-grid"
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
                >
                    {visible.map((p, i) => (
                        <ProductCard key={p.id} product={p} index={i} />
                    ))}
                </div>
            )}
        </section>
    );
}
